import type { DateSpec } from '@taskos/shared';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { useId, useState } from 'react';
import { post } from '../../api/client';
import { useDatePresets } from '../../api/queries';
import { Spinner, Toggle } from '../../components/ui';
import { useDebounced } from '../../lib/hooks';
import { formatDate, formatTime } from '../../lib/format';

interface ResolveResult {
  date: string | null;
  time: string | null;
  day?: { working: boolean; reason: string; holidayName?: string };
}

/**
 * Date presets + working-day toggle. The actual resulting date for every option is computed by the
 * server's working-day engine and shown under each button, so the user never calculates dates.
 */
export function DatePicker({
  value,
  onChange,
  allowNone = true,
  label = 'Due date',
  showTime = true,
  compact,
}: {
  value: DateSpec;
  onChange: (v: DateSpec) => void;
  allowNone?: boolean;
  label?: string;
  showTime?: boolean;
  compact?: boolean;
}) {
  const presets = useDatePresets();
  const id = useId();
  const working = value.mode === 'RELATIVE' ? value.workingDays : false;
  const [wdToggle, setWdToggle] = useState(working);
  const [customWD, setCustomWD] = useState<string>(value.mode === 'RELATIVE' && ![0, 1, 3, 5, 7, 10, 14, 30, 60, 90].includes(value.amount) ? String(value.amount) : '');
  const time = value.mode === 'NONE' ? null : (value.time ?? null);
  const debouncedSpec = useDebounced(value, 200);
  const resolved = useQuery({
    queryKey: ['resolve', debouncedSpec],
    queryFn: () => post<ResolveResult>('/api/dates/resolve', debouncedSpec),
    enabled: debouncedSpec.mode !== 'NONE',
    staleTime: 30_000,
  });

  const setRelative = (amount: number, wd = wdToggle) => onChange({ mode: 'RELATIVE', amount, workingDays: wd, time });
  const toggleWD = (v: boolean) => {
    setWdToggle(v);
    if (value.mode === 'RELATIVE') onChange({ ...value, workingDays: v });
  };
  const list = presets.data?.presets ?? [];
  const shown = compact ? list.filter((p) => [0, 1, 3, 7, 14, 30].includes(p.amount)) : list;

  return (
    <div className="stack sm" role="group" aria-labelledby={`${id}-label`}>
      <div className="flex between center wrap gap-sm">
        <span id={`${id}-label`} className="label">
          {label}
        </span>
        <Toggle checked={wdToggle} onChange={toggleWD} label={`Working days`} description={wdToggle ? 'Counting working days only (holidays & off-Saturdays skipped)' : 'Counting calendar days'} />
      </div>
      {presets.isPending ? (
        <Spinner label="Loading dates" />
      ) : presets.isError ? (
        <span className="error-text">Could not load date options.</span>
      ) : (
        <div className="date-grid" role="radiogroup" aria-label={label}>
          {allowNone && (
            <button type="button" role="radio" className="date-btn" aria-checked={value.mode === 'NONE'} onClick={() => onChange({ mode: 'NONE' })}>
              <strong>No date</strong>
              <span>Anytime</span>
            </button>
          )}
          {shown.map((p) => {
            const r = wdToggle ? p.working : p.calendar;
            const checked = value.mode === 'RELATIVE' && value.amount === p.amount && value.workingDays === wdToggle;
            const name = p.amount === 0 ? 'Today' : p.amount === 1 && !wdToggle ? 'Tomorrow' : `+${p.amount}${wdToggle ? ' WD' : ' days'}`;
            return (
              <button key={p.id} type="button" role="radio" className="date-btn" aria-checked={checked} onClick={() => setRelative(p.amount)} title={r.label}>
                <strong>{name}</strong>
                <span>{formatDate(r.date)}</span>
              </button>
            );
          })}
        </div>
      )}
      <div className="inline-row">
        <label className="inline-row small" htmlFor={`${id}-custom`}>
          <CalendarDays size={15} aria-hidden /> Custom date
        </label>
        <input
          id={`${id}-custom`}
          type="date"
          className="input"
          style={{ width: 170 }}
          value={value.mode === 'DATE' ? value.date : ''}
          onChange={(e) => (e.target.value ? onChange({ mode: 'DATE', date: e.target.value, time }) : allowNone && onChange({ mode: 'NONE' }))}
        />
        {wdToggle && (
          <>
            <label className="small" htmlFor={`${id}-wd`}>
              or +
            </label>
            <input
              id={`${id}-wd`}
              type="number"
              min={1}
              max={3660}
              inputMode="numeric"
              className="input"
              style={{ width: 86 }}
              placeholder="N"
              value={customWD}
              onChange={(e) => {
                setCustomWD(e.target.value);
                const n = Number(e.target.value);
                if (Number.isInteger(n) && n >= 1 && n <= 3660) setRelative(n, true);
              }}
              aria-label="Custom number of working days"
            />
            <span className="small">working days</span>
          </>
        )}
        {showTime && value.mode !== 'NONE' && (
          <>
            <label className="small" htmlFor={`${id}-time`}>
              Time
            </label>
            <input
              id={`${id}-time`}
              type="time"
              className="input"
              style={{ width: 120 }}
              value={time ?? ''}
              onChange={(e) => onChange({ ...value, time: e.target.value || null } as DateSpec)}
            />
          </>
        )}
      </div>
      {value.mode !== 'NONE' && (
        <div className="resolved-date" aria-live="polite">
          <CalendarDays size={16} aria-hidden />
          {resolved.isFetching && !resolved.data ? (
            <span className="muted">Calculating…</span>
          ) : resolved.isError ? (
            <span className="error-text">{(resolved.error as Error).message}</span>
          ) : resolved.data?.date ? (
            <span>
              <strong>{formatDate(resolved.data.date, true)}</strong>
              {resolved.data.time && ` · ${formatTime(resolved.data.time)}`}
              {value.mode === 'RELATIVE' && <span className="muted"> · +{value.amount} {value.workingDays ? 'working' : 'calendar'} days</span>}
              {resolved.data.day && !resolved.data.day.working && (
                <span className="badge warning" style={{ marginLeft: 8 }}>
                  {resolved.data.day.holidayName ? `Holiday: ${resolved.data.day.holidayName}` : 'Non-working day'}
                </span>
              )}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
